import { FC, useEffect, useRef, useState } from 'react';

import { Box, HStack, Icon, Text, useBreakpointValue } from '@chakra-ui/react';
import { AudioLines, Mic, PhoneOff, Square } from 'lucide-react';

import { IconButton, WorkBeam } from '@/components/core';
import { snackbar } from '@/components/features/Snackbar';
import { cn } from '@/lib/utils';

import { Editor, EditorHandle, SuggestionItemGroup } from '../Editor';
import { CommandPicker } from './CommandPicker';
import { SendMessageIcon } from './SendMessageIcon';
import { VoiceCallBar, VoiceCallControl } from './VoiceCallBar';

const VOICE_MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/mp4;codecs=mp4a.40.2',
];

const pickSupportedMimeType = () => {
  if (
    typeof MediaRecorder === 'undefined' ||
    typeof MediaRecorder.isTypeSupported !== 'function'
  ) {
    return '';
  }

  return (
    VOICE_MIME_CANDIDATES.find(type => MediaRecorder.isTypeSupported(type)) ??
    ''
  );
};

const formatRecordingDuration = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

const EMPTY_SUGGESTIONS: SuggestionItemGroup[] = [];

type Props = {
  className?: string;
  isLoading?: boolean;
  placeholder?: string;
  onSendMessage?: (message: string) => void;
  suggestions?: SuggestionItemGroup[];
  enableVoice?: boolean;
  onSendVoiceNote?: (blob: Blob) => void;
  /** Hands-free call with the agent, beside the voice-note button. */
  voiceCall?: VoiceCallControl;
  onAbort?: () => void;
};

const PromptInputWithActions: FC<Props> = ({
  className,
  isLoading = false,
  placeholder,
  onSendMessage,
  suggestions = EMPTY_SUGGESTIONS,
  enableVoice = false,
  onSendVoiceNote,
  voiceCall,
  onAbort,
}) => {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const [input, setInput] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const editorRef = useRef<EditorHandle>(null);
  const suggestionRef = useRef<HTMLDivElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const shouldSendRecordingRef = useRef(false);

  const stopMediaTracks = () => {
    mediaStreamRef.current?.getTracks().forEach(track => track.stop());
    mediaStreamRef.current = null;
  };

  const resetRecorder = () => {
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    shouldSendRecordingRef.current = false;
    setIsRecording(false);
    setRecordingSeconds(0);
    stopMediaTracks();
  };

  useEffect(() => {
    if (!isRecording) return;

    const timer = window.setInterval(() => {
      setRecordingSeconds(seconds => seconds + 1);
    }, 1000);

    return () => window.clearInterval(timer);
  }, [isRecording]);

  useEffect(
    () => () => {
      shouldSendRecordingRef.current = false;
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== 'inactive'
      ) {
        mediaRecorderRef.current.stop();
      }
      stopMediaTracks();
    },
    []
  );

  const sendMessage = (message: string) => {
    if (onSendMessage) {
      onSendMessage(message);
    }
    setInput('');
    if (!isMobile) {
      inputRef.current?.focus();
    }
  };

  const handleSend = () => {
    if (input.trim() && !isLoading && !isRecording) {
      sendMessage(input);
    }
  };

  const handlePrimaryClick = () => {
    if (isLoading) {
      onAbort?.();
      return;
    }
    handleSend();
  };

  const startRecording = async () => {
    if (isLoading || isRecording || !onSendVoiceNote) return;

    if (
      typeof navigator === 'undefined' ||
      !navigator.mediaDevices?.getUserMedia
    ) {
      snackbar({
        title: 'Microphone unavailable',
        description: 'Voice notes are not supported in this browser.',
        type: 'error',
      });
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      chunksRef.current = [];

      const mimeType = pickSupportedMimeType();
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      mediaRecorderRef.current = recorder;
      shouldSendRecordingRef.current = true;

      recorder.ondataavailable = event => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const shouldSend = shouldSendRecordingRef.current;
        const blobType = recorder.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: blobType });
        resetRecorder();

        if (shouldSend && blob.size > 0) {
          onSendVoiceNote(blob);
        }
      };

      recorder.start();
      setRecordingSeconds(0);
      setIsRecording(true);
    } catch {
      resetRecorder();
      snackbar({
        title: 'Microphone access needed',
        description: 'Allow microphone access to send a voice note.',
        type: 'error',
      });
    }
  };

  const stopRecording = (shouldSend: boolean) => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') {
      resetRecorder();
      return;
    }

    shouldSendRecordingRef.current = shouldSend;
    recorder.stop();
  };

  const handleVoiceClick = () => {
    if (isRecording) {
      stopRecording(true);
      return;
    }
    void startRecording();
  };

  const isInCall = Boolean(voiceCall && voiceCall.status !== 'off');
  const editorDisabled = isLoading || isRecording || isInCall;
  const canAbort = Boolean(onAbort);
  const primaryDisabled = isLoading
    ? !canAbort
    : !input.trim() || isRecording || isInCall;
  const hasCommands = suggestions.some(group => group.items.length > 0);
  const showCommandPicker = hasCommands && !isRecording && !isInCall;
  const voiceButtonCount = (enableVoice ? 1 : 0) + (voiceCall ? 1 : 0);
  const callAgentName = voiceCall?.agentName ?? 'Connie';

  const handleInsertCommand = (item: string, icon?: string) => {
    editorRef.current?.insertSlashCommand(item, icon);
  };

  return (
    <Box
      ref={suggestionRef}
      display='flex'
      flexDirection='column'
      width='full'
      border='none'
      mb='0'
      className={cn('group/input', className)}
    >
      <WorkBeam preset='line' active={isLoading} borderRadius={12}>
        <Editor
          ref={editorRef}
          disabled={editorDisabled}
          onChange={setInput}
          value={input}
          onSubmit={handleSend}
          placeholder={isRecording || isInCall ? '' : placeholder}
          suggestions={suggestions}
          contentPaddingLeft={showCommandPicker ? 48 : 12}
          contentPaddingRight={48 + voiceButtonCount * 40}
        >
          {voiceCall && isInCall && <VoiceCallBar call={voiceCall} />}
          {isRecording && (
            <HStack
              position='absolute'
              insetY={0}
              left={0}
              right={0}
              px={4}
              pr='96px'
              spacing={3}
              bg='gray.800'
              borderRadius='inherit'
              pointerEvents='none'
              zIndex={2}
              aria-live='polite'
            >
              <Box
                boxSize={2}
                flexShrink={0}
                borderRadius='full'
                bg='red.400'
                className='animate-pulse'
              />
              <Text color='white' fontSize='sm' fontWeight='semibold'>
                Recording
              </Text>
              <Text
                color='whiteAlpha.700'
                fontSize='sm'
                sx={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {formatRecordingDuration(recordingSeconds)}
              </Text>
              <Text
                display={{ base: 'none', md: 'block' }}
                color='whiteAlpha.500'
                fontSize='xs'
              >
                Tap stop to send
              </Text>
            </HStack>
          )}
          {showCommandPicker && (
            <HStack position='absolute' left={2} bottom={2} spacing={1}>
              <CommandPicker
                suggestions={suggestions}
                disabled={editorDisabled}
                onSelect={handleInsertCommand}
              />
            </HStack>
          )}
          <HStack position='absolute' right={2} bottom={2} spacing={1}>
            {enableVoice && (
              <IconButton
                variant='glassy'
                size='sm'
                colorScheme={isRecording ? 'red' : 'gray'}
                zIndex={3}
                bg={isRecording ? 'red.500' : undefined}
                color={isRecording ? 'white' : undefined}
                _hover={isRecording ? { bg: 'red.400' } : undefined}
                icon={
                  isRecording ? (
                    <Square size={16} className='fill-current' />
                  ) : (
                    <Mic size={16} />
                  )
                }
                disabled={isLoading || isInCall}
                onClick={handleVoiceClick}
                aria-label={
                  isRecording ? 'Stop recording' : 'Record voice note'
                }
              />
            )}
            {voiceCall && (
              <IconButton
                variant='glassy'
                size='sm'
                colorScheme='gray'
                zIndex={3}
                icon={
                  isInCall ? <PhoneOff size={16} /> : <AudioLines size={16} />
                }
                disabled={!isInCall && (isLoading || isRecording)}
                onClick={isInCall ? voiceCall.onStop : voiceCall.onStart}
                aria-label={isInCall ? 'End call' : `Talk to ${callAgentName}`}
                title={
                  isInCall ? 'End call' : `Talk to ${callAgentName}, hands-free`
                }
              />
            )}
            <IconButton
              variant='glassy'
              size='sm'
              colorScheme='gray'
              zIndex={3}
              icon={
                isLoading ? (
                  <Square size={16} className='fill-current' />
                ) : (
                  <Icon
                    as={SendMessageIcon}
                    boxSize={3}
                    className='text-primary'
                  />
                )
              }
              disabled={primaryDisabled}
              onClick={handlePrimaryClick}
              aria-label={isLoading ? 'Stop response' : 'Send message'}
            />
          </HStack>
        </Editor>
      </WorkBeam>
    </Box>
  );
};

export default PromptInputWithActions;
